import { Component, type ErrorInfo, type ReactNode } from "react";
import styles from "./ErrorBoundary.module.css";

type AppErrorBoundaryProps = {
  children: ReactNode;
  resetKey: string;
};

type AppErrorBoundaryState = {
  error: Error | null;
};

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = {
    error: null,
  };

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { error };
  }

  componentDidUpdate(previousProps: AppErrorBoundaryProps) {
    if (this.state.error && previousProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Unhandled Bastion webapp error", error, info);
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.assign("/");
  };

  render() {
    const { children } = this.props;
    const { error } = this.state;

    if (!error) {
      return children;
    }

    return (
      <main className={styles.page} role="alert">
        <section className={styles.card}>
          <p className={styles.eyebrow}>Something went wrong</p>
          <h1 className={styles.title}>Bastion hit an unexpected error.</h1>
          <p className={styles.description}>
            The app is still running, but this view crashed. Reload the page or return home to
            continue working.
          </p>
          <div className={styles.actions}>
            <button className={styles.primaryAction} type="button" onClick={this.handleReload}>
              Reload
            </button>
            <button className={styles.secondaryAction} type="button" onClick={this.handleGoHome}>
              Go home
            </button>
          </div>
          {import.meta.env.DEV ? <pre className={styles.details}>{error.message}</pre> : null}
        </section>
      </main>
    );
  }
}
